import React, { useEffect, useMemo, useState } from "react";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import { addMontrealCalendarDays, montrealYmdFromDate } from "../../../pages/components/montrealCalendar";
import { WORD_WHEEL_DAILY_CALENDAR_MIN } from "../../../prototype/mobile-word-wheel/routes";
import Logger from "../../../services/Logger";
import AdminWordWheelService from "./AdminWordWheelService";
import WordWheelTplGridCard from "./WordWheelTplGridCard";

function ymdFromDayjs(value) {
    if (!value || !value.isValid?.()) return montrealYmdFromDate();
    return value.format("YYYY-MM-DD");
}

export default function WordWheelDailyCalendarView({ onOpen, onLayoutSaved, onNotify }) {
    const todayYmd = useMemo(() => montrealYmdFromDate(), []);
    const minYmd = WORD_WHEEL_DAILY_CALENDAR_MIN;
    const [selectedYmd, setSelectedYmd] = useState(todayYmd);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [puzzle, setPuzzle] = useState(null);
    const [poolSize, setPoolSize] = useState(0);
    const [fillingClues, setFillingClues] = useState(false);

    useEffect(() => {
        let cancelled = false;
        AdminWordWheelService.requestList(
            { page: 0, pageSize: 1 },
            [{ field: "createdAt", sort: "asc" }],
            () => {},
            (count) => {
                if (!cancelled) setPoolSize(Number(count) || 0);
            },
            "",
            "PUBLISHED",
            "english",
            "none",
        ).catch((err) => {
            Logger.error("Failed to count daily puzzle pool:", err);
        });
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError("");
        setPuzzle(null);
        AdminWordWheelService.previewDaily(selectedYmd)
            .then((data) => {
                if (cancelled) return;
                if (data?.code === "NO_DATA" || !data?.id) {
                    setError("No daily puzzle for this date.");
                    return;
                }
                setPuzzle(data);
            })
            .catch((err) => {
                Logger.error("Failed to load daily puzzle for calendar:", err);
                if (!cancelled) setError(err?.message || "Failed to load daily puzzle.");
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [selectedYmd]);

    const calendarValue = useMemo(() => dayjs(selectedYmd), [selectedYmd]);
    const minDayjs = useMemo(() => dayjs(minYmd), [minYmd]);
    const lastYmd = poolSize > 0 ? addMontrealCalendarDays(minYmd, poolSize - 1) : "";
    const lastDateLabel = lastYmd ? lastYmd.replace(/-/g, "/") : "";
    const isToday = selectedYmd === todayYmd;
    const dateLabel = selectedYmd.replace(/-/g, "/");
    const canGoPrev = selectedYmd > minYmd;

    const shiftDay = (delta) => {
        const next = addMontrealCalendarDays(selectedYmd, delta);
        setSelectedYmd(next < minYmd ? minYmd : next);
    };

    const handleLayoutSaved = (tplId, coords) => {
        setPuzzle((prev) => {
            if (!prev || String(prev.id) !== String(tplId)) {
                return prev;
            }
            return {
                ...prev,
                filledCoordinates: typeof coords === "string" ? coords : JSON.stringify(coords),
            };
        });
        onLayoutSaved?.(tplId, coords);
    };

    const handleFillMissingClues = async (row) => {
        if (!row?.id || fillingClues) {
            return;
        }
        setFillingClues(true);
        try {
            const result = await AdminWordWheelService.fillDisplayCluesFromDictionary(row.id);
            const updated = result?.wordWheelTpl;
            const filled = Number(result?.filled) || 0;
            const stillMissing = Array.isArray(result?.stillMissing) ? result.stillMissing : [];
            if (updated) {
                setPuzzle((prev) => (prev && String(prev.id) === String(row.id)
                    ? { ...prev, displayClue: updated.displayClue }
                    : prev));
            }
            if (filled > 0 && stillMissing.length === 0) {
                onNotify?.("success", `Filled ${filled} clue(s) from the dictionary.`);
            } else if (filled > 0) {
                onNotify?.(
                    "warning",
                    `Filled ${filled} clue(s). Still missing: ${stillMissing.join(", ")}.`
                );
            } else if (stillMissing.length > 0) {
                onNotify?.(
                    "warning",
                    `No dictionary meanings found for: ${stillMissing.join(", ")}.`
                );
            } else {
                onNotify?.("info", "All words already have clues.");
            }
        } catch (error) {
            Logger.error("Failed to fill word wheel clues from dictionary:", error);
            onNotify?.("error", error?.message || "Failed to fill clues from the dictionary.");
        } finally {
            setFillingClues(false);
        }
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {lastDateLabel ? (
                <Typography variant="body1" sx={{ color: "text.secondary" }}>
                    Daily coverage through <strong>{lastDateLabel}</strong>
                    {poolSize > 0 ? ` (day ${poolSize})` : ""}. The published non-season English
                    pool can uniquely cover every phone daily from {minYmd.replace(/-/g, "/")} through
                    this date. After {lastDateLabel}, the catalog repeats.
                </Typography>
            ) : null}
            <Box
                sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 2,
                    alignItems: "flex-start",
                }}
            >
                <Paper elevation={2} sx={{ p: 1 }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DateCalendar
                            value={calendarValue}
                            minDate={minDayjs}
                            onChange={(next) => {
                                if (!next) return;
                                const ymd = ymdFromDayjs(next);
                                setSelectedYmd(ymd < minYmd ? minYmd : ymd);
                            }}
                        />
                    </LocalizationProvider>
                </Paper>
                <Box sx={{ flex: "1 1 280px", maxWidth: 400, minWidth: 260 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5, minHeight: 44 }}>
                        <IconButton
                            aria-label="Previous day"
                            onClick={() => shiftDay(-1)}
                            disabled={!canGoPrev || loading}
                            sx={{ minWidth: 44, minHeight: 44 }}
                        >
                            <ChevronLeftIcon />
                        </IconButton>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            {dateLabel}
                        </Typography>
                        {isToday ? <Chip size="small" color="success" label="Today" /> : null}
                        {puzzle?.dailyDayNumber != null ? (
                            <Chip size="small" variant="outlined" label={`Daily #${puzzle.dailyDayNumber}`} />
                        ) : null}
                        <IconButton
                            aria-label="Next day"
                            onClick={() => shiftDay(1)}
                            disabled={loading}
                            sx={{ minWidth: 44, minHeight: 44, ml: "auto" }}
                        >
                            <ChevronRightIcon />
                        </IconButton>
                    </Box>
                    {loading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                            <CircularProgress size={32} />
                        </Box>
                    ) : error ? (
                        <Paper sx={{ py: 4, px: 2, textAlign: "center" }}>
                            <Typography color="text.secondary">{error}</Typography>
                        </Paper>
                    ) : puzzle ? (
                        <WordWheelTplGridCard
                            row={puzzle}
                            hideSelect
                            isTodayDaily={isToday}
                            onOpen={onOpen}
                            onLayoutSaved={handleLayoutSaved}
                            onNotify={onNotify}
                            onFillMissingClues={handleFillMissingClues}
                            fillingClues={fillingClues}
                        />
                    ) : null}
                </Box>
            </Box>
        </Box>
    );
}
